import { queryClient, usersQueries } from '@/api'
import { findUserByEmail, loadDirectory, normalizeEmail } from '@/modules/accounts'
import type { User } from '@/modules/accounts'
import { signedIn, signedOut } from '@/modules/auth/store/auth-slice'
import { appConfig } from '@/config/app-config'
import { SESSION_KEY } from '@/store/shared-state'
import type { AppThunk } from '@/store/create-store'

/**
 * HARDCODED ON PURPOSE. This code is public and accepted for every account; it is a placeholder for the OTP step, not a
 * credential, and must never be used for production authentication (real one-time codes, rate limiting, server-side sessions).
 */
export const DEMO_OTP = appConfig.demoOtp

/**
 * First login step: loads the accounts and users from the backend and checks the email belongs to an active user. Throws a
 * user-facing message otherwise (or the backend's own, when it cannot be reached).
 */
export function lookupUser(email: string): AppThunk<Promise<User>> {
  return async (dispatch, getState) => {
    // Ask the backend for this email only; nobody who has not signed in gets to load the user list.
    const wanted = normalizeEmail(email)
    const found = (await queryClient.fetchQuery(usersQueries.all({ search: wanted }))).find((candidate) => normalizeEmail(candidate.email) === wanted)
    if (!found) throw new Error('No account matches this email.')
    if (!found.isActive) throw new Error('This account has been deactivated.')
    await dispatch(loadDirectory(found.id))
    const user = findUserByEmail(getState(), email)
    if (!user) throw new Error('No account matches this email.')
    return user
  }
}

/** Starts this tab's session for a user already in the directory. Kept in `sessionStorage`, so other tabs stay signed in as whoever they are. */
export function startSession(email: string): AppThunk<User> {
  return (dispatch, getState, ctx) => {
    const user = findUserByEmail(getState(), email)
    if (!user) throw new Error('No account matches this email.')
    ctx.session.setItem(SESSION_KEY, user.id)
    dispatch(signedIn({ userId: user.id, signedInAt: new Date().toISOString() }))
    return user
  }
}

/**
 * Second login step. The code is checked here against `DEMO_OTP` (the one hardcoded part of sign-in); the user comes from
 * the backend. Throws a user-facing message on a wrong code.
 */
export function signIn(email: string, code: string): AppThunk<Promise<User>> {
  return async (dispatch, getState) => {
    if (!findUserByEmail(getState(), email)) await dispatch(lookupUser(email))
    if (code !== DEMO_OTP) throw new Error('That code is incorrect. Try again.')
    return dispatch(startSession(email))
  }
}

/** Ends only this tab's session. Shared data and other tabs' sessions are untouched. */
export function signOut(): AppThunk {
  return (dispatch, _getState, ctx) => {
    ctx.session.removeItem(SESSION_KEY)
    // What was loaded belongs to the person who has just left: the next sign-in must start from nothing.
    queryClient.clear()
    dispatch(signedOut())
  }
}
